import '../../App.css';
import { useAuth } from '../../AuthContext';
import { useState } from 'react';
import React from 'react';
import MainStructure from '../../components/general/MainStructure';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { PlatformIcon, ProfilePicture } from '../../components/general/AccountIcons';

import ximage from '../../assets/twitter.png';
import linkedinimage from '../../assets/linkedin.png';
import instagramimage from '../../assets/instagram.png';
import youtubeimage from '../../assets/youtube.png';
import tiktok from '../../assets/tiktok.png';
import threads from '../../assets/threads.png';
import facebook from '../../assets/facebook.png';
import reddit from '../../assets/reddit.png';
import bluesky from '../../assets/bluesky.png';
import discord from '../../assets/discord.png';
import skool from '../../assets/skool.png';
import pinterest from '../../assets/pinterest.png';
import myImage from '../../assets/empty-box-2.png';

import TwitterAuth from '../../components/general/TwitterAuth';
import { LinkedInConnectButton, LinkedInReconnectButton } from '../../components/general/LinkedInAuth';
import { YouTubeConnectButton, YouTubeReconnectButton } from '../../components/general/YouTubeAuth';
import TikTokAuth from '../../components/general/TikTokAuth';
import { InstagramConnect } from '../../components/general/InstagramConnect';
import { ThreadsConnect } from '../../components/general/ThreadsConnect';
import { FacebookConnect } from '../../components/general/FacebookConnect';
import { BlueskyConnect } from '../../components/general/BlueskyConnect';
import { PinterestConnect } from '../../components/general/PinterestConnect';
import { DiscordConnect } from '../../components/general/DiscordConnect';
import { SkoolConnect, ExtensionBanner } from '../../components/general/SkoolConnect';

const platforms = [
  { name: 'X / Twitter', key: 'twitter', icon: ximage, component: <TwitterAuth /> },
  { name: 'LinkedIn', key: 'linkedin', icon: linkedinimage, component: <LinkedInConnectButton /> },
  { name: 'Instagram', key: 'instagram', icon: instagramimage, component: <InstagramConnect /> },
  { name: 'TikTok', key: 'tiktok', icon: tiktok, component: <TikTokAuth /> },
  { name: 'YouTube', key: 'youtube', icon: youtubeimage, component: <YouTubeConnectButton /> },
  { name: 'Threads', key: 'threads', icon: threads, component: <ThreadsConnect /> },
  { name: 'Facebook', key: 'facebook', icon: facebook, component: <FacebookConnect /> },
  { name: 'Reddit', key: 'reddit', icon: reddit, component: <button className="btn btn-sm w-100" style={{ border: '1px solid #d1d9e0', borderRadius: 7, color: '#475569' }}>Connect Reddit</button> },
  { name: 'Bluesky', key: 'bluesky', icon: bluesky, component: <BlueskyConnect /> },
  { name: 'Pinterest', key: 'pinterest', icon: pinterest, component: <PinterestConnect /> },
  // Through the Chrome extension: Skool has no public API.
  { name: 'Skool', key: 'skool', icon: skool, component: <SkoolConnect /> },
];

// Discord is built but paused until the bot is configured in the Discord portal
// and wp-config.php. Set this to true to show the Connect Discord card again.
const DISCORD_ENABLED = false;
if (DISCORD_ENABLED) {
  platforms.push({ name: 'Discord', key: 'discord', icon: discord, component: <DiscordConnect /> });
}

const s = {
  sectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#7d8fa3',
    marginBottom: 12,
  },
  emptyBox: {
    background: '#f8fafc',
    border: '1px dashed #d1d9e0',
    borderRadius: 12,
    padding: '40px 24px',
    textAlign: 'center',
  },
};

function SocialAccounts() {
  const myContext = useAuth();
  const mySocialAccounts = myContext.accounts;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (account) => {
    setAccountToDelete(account);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!accountToDelete) return;
    setDeleting(true);
    try {
      myContext.deleteAccount(accountToDelete.id, accountToDelete.type);
      toast.success('Account removed.');
      setShowDeleteModal(false);
      setAccountToDelete(null);
    } catch {
      toast.error('Failed to remove account.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <MainStructure activeItem="social-accounts">
      <div className="container-fluid px-0">

        {/* ── Header ───────────────────────────────────────────── */}
        <div className="mb-4">
          <h1 style={{ fontSize: '1.3rem', fontWeight: 500, color: '#0f172a', margin: 0 }}>
            Social Accounts
          </h1>
          <p style={{ fontSize: '0.85rem', color: '#7d8fa3', margin: 0 }}>
            Connect and manage your social media accounts
          </p>
        </div>

        <ExtensionBanner />

        {/* ── Connect platforms ────────────────────────────────── */}
        <p style={s.sectionTitle}>Connect a platform</p>
        <div className="row g-3 mb-5">
          {platforms.map((platform) => (
            <div key={platform.key} className="col-12 col-sm-6 col-md-4 col-lg-3">
              <div style={{
                background: '#fff',
                border: '1px solid #d1d9e0',
                borderRadius: 12,
                padding: '20px 16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                height: '100%',
              }}>
                <img
                  src={platform.icon}
                  alt={platform.name}
                  style={{ width: 40, height: 40, objectFit: 'contain', marginBottom: 10 }}
                  onError={e => { e.target.onerror = null; e.target.style.opacity = 0; }}
                />
                <p style={{ fontSize: '0.85rem', fontWeight: 500, color: '#334155', marginBottom: 14 }}>
                  {platform.name}
                </p>
                <div style={{ width: '100%', marginTop: 'auto' }}>
                  {platform.component}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Connected accounts ────────────────────────────────── */}
        <p style={s.sectionTitle}>Connected accounts</p>

        {mySocialAccounts.length === 0 ? (
          <div style={s.emptyBox}>
            <img src={myImage} alt="" style={{ width: 56, opacity: 0.35, marginBottom: 12 }} />
            <p style={{ color: '#7d8fa3', fontSize: '0.9rem', margin: 0 }}>
              No accounts connected yet.
            </p>
          </div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #d1d9e0', borderRadius: 12, overflow: 'hidden' }}>
            {mySocialAccounts.map((account, i) => (
              <div
                key={`${account.id}_${account.type}`}
                className="d-flex align-items-center"
                style={{
                  padding: '12px 16px',
                  borderBottom: i < mySocialAccounts.length - 1 ? '1px solid #f1f5f9' : 'none',
                }}
              >
                {/* Combined icon */}
                <div style={{ position: 'relative', width: 44, height: 44, flexShrink: 0, marginRight: 12 }}>
                  <PlatformIcon type={account.type} size={40} />
                  <div style={{
                    position: 'absolute', bottom: -4, right: -4,
                    border: '2px solid #fff', borderRadius: '50%', background: '#fff',
                  }}>
                    <ProfilePicture src={account.image} name={account.name} size={22} />
                  </div>
                </div>

                {/* Name */}
                <span style={{ fontSize: '0.9rem', color: '#334155', fontWeight: 500, flexGrow: 1, minWidth: 0 }} className="text-truncate">
                  {account.name}
                  {account.disconnected && (
                    <span style={{ display: 'block', fontSize: '0.75rem', color: '#dc2626', fontWeight: 400 }}>
                      SchedPilot lost access to this channel. Reconnect to keep posting.
                    </span>
                  )}
                  {account.type === 'skool' && account.needs_reconnect && (
                    <span style={{ display: 'block', fontSize: '0.75rem', color: '#dc2626', fontWeight: 400 }}>
                      <span style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 6, padding: '1px 6px', fontWeight: 600, marginRight: 6 }}>Needs reconnect</span>
                      Skool logged SchedPilot out. Reconnect from the SchedPilot extension.
                    </span>
                  )}
                </span>

                {/* Type badge */}
                <span style={{
                  fontSize: '0.72rem', color: '#64748b',
                  background: '#f1f5f9', borderRadius: 6,
                  padding: '2px 8px', marginRight: 10, textTransform: 'capitalize',
                }}>
                  {account.type === 'linkedin_page' ? 'LinkedIn Page' : account.type}
                </span>

                {/* Actions */}
                <div className="d-flex align-items-center gap-2">
                  {account.type === 'youtube' && <YouTubeReconnectButton accountId={account.id} />}
                  {account.type === 'linkedin' && <LinkedInReconnectButton accountId={account.id} />}
                  {account.type === 'discord' && account.disconnected && <DiscordConnect label="Reconnect" className="btn btn-sm" solid={false} />}
                  {account.type === 'skool' && account.needs_reconnect && <SkoolConnect label="Reconnect" compact />}
                  <button
                    className="btn btn-sm sp-btn-danger"
                    style={{ border: '1px solid #fecaca', borderRadius: 7, color: '#dc2626', background: '#fff', fontSize: '0.78rem' }}
                    onClick={() => handleDeleteClick(account)}
                  >
                    <Icon.Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Delete modal ──────────────────────────────────────── */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: 12, border: 'none' }}>
              <div className="modal-header" style={{ border: 'none', paddingBottom: 0 }}>
                <h6 className="modal-title" style={{ fontWeight: 500 }}>Remove account?</h6>
                <button className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body" style={{ fontSize: '0.875rem', color: '#64748b' }}>
                Remove <strong>{accountToDelete?.name}</strong> from SchedPilot?
              </div>
              <div className="modal-footer" style={{ border: 'none' }}>
                <button
                  className="btn btn-sm sp-btn-ghost"
                  style={{ border: '1px solid #d1d9e0', borderRadius: 8 }}
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  style={{ borderRadius: 8 }}
                  onClick={confirmDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Removing…' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainStructure>
  );
}

export default SocialAccounts;
